import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton, useToast } from '../components';
import { useRefresh } from './DataProvider';
import { useTheme } from './ThemeProvider';
import { useMe } from './Session';
import { AccountSheet } from './AccountSheet';
import { titleFor } from './Nav';
import { IconMoon, IconRefresh, IconSun } from './icons';
import { businessDateOf, clockDate, clockTime, clockTimeShort, formatDate, initials } from '../lib/format';
import './shell.css';
import { Mark } from './Mark';

/**
 * Persistent chrome, on every screen: Refresh, theme, account (which holds
 * Admin and both sign-outs).
 *
 * Refresh is deliberately not `location.reload()` — it cancels in-flight
 * requests, purges the cache and refetches. Press and hold for a hard reset
 * that also clears storage and the service worker, for a genuinely wedged view.
 */
export function Header() {
  const { pathname } = useLocation();
  const { refresh, hardReset, refreshing } = useRefresh();
  const { resolved, toggle } = useTheme();
  const { me } = useMe();
  const toast = useToast();
  const [accountOpen, setAccountOpen] = useState(false);
  const held = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  // `pointer` records that this activation began as a press, so the click that
  // follows a pointer-up is not counted twice. A keyboard activation fires
  // `onClick` with no preceding pointer-down, and takes the plain path.
  const pointer = useRef(false);

  const startHold = () => {
    pointer.current = true;
    held.current = false;
    timer.current = window.setTimeout(() => {
      held.current = true;
      void hardReset().then(() => toast({
        title: 'Hard reset',
        message: 'Cache, local storage and the service worker were cleared.',
        tone: 'warn',
      }));
    }, 650);
  };

  const endHold = () => {
    window.clearTimeout(timer.current);
    if (!held.current) refresh();
  };

  const cancelHold = () => {
    window.clearTimeout(timer.current);
    pointer.current = false;
  };

  /** Enter and Space on the focused control. A press has already refreshed. */
  const activate = () => {
    if (pointer.current) { pointer.current = false; return; }
    refresh();
  };

  return (
    <header className="sh-header">
      <div className="sh-header__brand">
        <Mark size={30} label={false} animate={false} />
      </div>

      <div>
        <div className="sh-header__title">{titleFor(pathname)}</div>
        <div className="sh-header__meta">Business night · {formatDate(businessDateOf())}</div>
      </div>

      <div className="sh-header__chrome no-print">
        <Clock />

        <IconButton
          label={refreshing ? 'Refreshing' : 'Refresh — hold for a hard reset'}
          onClick={activate}
          onPressStart={startHold}
          onPressEnd={endHold}
          onPressCancel={cancelHold}
        >
          <IconRefresh size={18} className={refreshing ? 'sh-refreshing' : undefined} />
        </IconButton>

        <IconButton
          label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={toggle}
        >
          {resolved === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
        </IconButton>

        <IconButton label="Account and settings" onClick={() => setAccountOpen(true)}>
          <span className="sh-user__avatar" style={{ width: 26, height: 26, fontSize: 10 }}>
            {initials(me?.displayName ?? '?')}
          </span>
        </IconButton>
      </div>

      <AccountSheet open={accountOpen} onClose={() => setAccountOpen(false)} />
    </header>
  );
}

/**
 * The wall clock in the chrome: Central date and time, ticking every second.
 *
 * Keyed to America/Chicago rather than to the device, because every figure on
 * this platform is cut on Chicago's clock — the 05:00 business boundary, the
 * night in play, "so far today". A viewer whose laptop is on Eastern reading a
 * Central business day needs the clock the day is measured by, not their own.
 *
 * It renders in its own component so the per-second tick re-renders the clock
 * and nothing else: the rest of the header, and every screen under it, are
 * untouched by it.
 */
function Clock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="sh-clock" role="status" aria-label={`Central time, ${clockTime(now)} on ${clockDate(now)}`}>
      <span className="sh-clock__date">{clockDate(now)}</span>
      <span className="sh-clock__time">{clockTime(now)}</span>
      <span className="sh-clock__time--sm">{clockTimeShort(now)}</span>
    </div>
  );
}
